"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InterpolationPipeline = void 0;
const ScriptEvaluator_1 = require("./ScriptEvaluator");
const utils_1 = require("../utils");
const RichTextParser_1 = require("../parser/RichTextParser");
const ScriptContext_1 = require("./ScriptContext");
const sharedDataRichText_1 = require("../utils/sharedDataRichText");
/**
 * Interpolation pipeline for resolving ${variable_name} placeholders in rich text
 * Uses precedence: scene → character → player → adventure → user
 */
class InterpolationPipeline {
    constructor(variableStore, adventureManager, friendsService, adventureInviteService, asyncOperationService, inviteLinkService, analyticsService, entitiesService, foreignEntityLookup) {
        this.variableStore = variableStore;
        this.adventureManager = adventureManager;
        this.friendsService = friendsService;
        this.adventureInviteService = adventureInviteService;
        this.asyncOperationService = asyncOperationService;
        this.inviteLinkService = inviteLinkService;
        this.analyticsService = analyticsService;
        this.entitiesService = entitiesService;
        this.foreignEntityLookup = foreignEntityLookup;
    }
    /** Attach or replace foreign library lookup (backend warms before scene present). */
    setForeignEntityLookup(lookup) {
        this.foreignEntityLookup = lookup;
    }
    /**
     * Set the adventure manager for script execution
     */
    setAdventureManager(adventureManager) {
        this.adventureManager = adventureManager;
    }
    /**
     * Interpolate variables in rich text description
     */
    interpolateRichText(description, context) {
        if (typeof description === 'string') {
            return this.interpolateString(description, context);
        }
        // Deep clone the description to avoid mutating the original
        const interpolatedDescription = {
            elements: this.interpolateElements(description.elements, context)
        };
        return interpolatedDescription;
    }
    /**
     * Interpolate variables in a string
     */
    interpolateString(text, context) {
        if (!text || typeof text !== 'string') {
            return text;
        }
        // Match ${variable_name} patterns
        const variablePattern = /\$\{([^}]+)\}/g;
        return text.replace(variablePattern, (match, variableName) => {
            const trimmedName = variableName.trim();
            if (context.injectedVariables &&
                Object.prototype.hasOwnProperty.call(context.injectedVariables, trimmedName)) {
                const injectedValue = context.injectedVariables[trimmedName];
                if (injectedValue === undefined || injectedValue === null) {
                    return '';
                }
                return String(injectedValue);
            }
            // Prefer current-entity variables when a current entity is set (same as script getVariable).
            if (this.entitiesService) {
                const cur = this.entitiesService.getCurrentEntityId(context.playerId);
                if (cur) {
                    const ev = this.entitiesService.getEntityVariable(cur, trimmedName);
                    if (ev !== undefined) {
                        return String(ev.value);
                    }
                }
            }
            const value = this.variableStore.getVariable(trimmedName, context);
            if (value === undefined || value === null) {
                // Variable not found during interpolation - return empty string
                return '';
            }
            return String(value);
        });
    }
    /**
     * Interpolate variables in an array of rich text elements
     */
    interpolateElements(elements, context) {
        const result = [];
        for (const element of elements) {
            if (element.type === 'conditional') {
                const evaluated = this.evaluateConditionalElement(element, context);
                if (evaluated.length > 0) {
                    result.push(...evaluated);
                }
                continue;
            }
            if (element.type === 'inline-option' && element.condition) {
                const conditionResult = this.evaluateInlineOptionCondition(element, context);
                if (!conditionResult) {
                    continue;
                }
            }
            const interpolated = this.interpolateElement(element, context);
            // If script returned rich text elements, expand them
            if (interpolated.type === 'script' && Array.isArray(interpolated.content)) {
                result.push(...interpolated.content);
            }
            else {
                result.push(interpolated);
            }
        }
        return result;
    }
    evaluateConditionalElement(element, context) {
        const noop = element.children ?? [];
        if (!context.player || !context.gameSession) {
            console.warn('Cannot evaluate <Conditional>: missing player or game session context');
            return this.interpolateElements(noop, context);
        }
        if (!element.condition || element.condition.trim() === '') {
            console.warn('Conditional element missing condition script');
            return this.interpolateElements(noop, context);
        }
        let conditionResult = false;
        try {
            conditionResult = ScriptEvaluator_1.ScriptEvaluator.executeCondition(element.condition, context.player, context.gameSession, this.variableStore, this.adventureManager, this.friendsService, this.adventureInviteService, this.asyncOperationService, this.inviteLinkService, undefined, undefined, undefined, this.analyticsService, this.entitiesService, this.foreignEntityLookup);
        }
        catch (error) {
            console.error('Failed to evaluate <Conditional> condition:', error);
            conditionResult = false;
        }
        const branch = conditionResult ? element.children ?? [] : element.elseChildren ?? [];
        if (!branch || branch.length === 0) {
            return [];
        }
        return this.interpolateElements(branch, context);
    }
    evaluateInlineOptionCondition(element, context) {
        if (!context.player || !context.gameSession) {
            console.warn('Cannot evaluate inline option condition: missing player or game session context');
            return true;
        }
        if (!element.condition || element.condition.trim() === '') {
            return true;
        }
        try {
            return ScriptEvaluator_1.ScriptEvaluator.executeCondition(element.condition, context.player, context.gameSession, this.variableStore, this.adventureManager, this.friendsService, this.adventureInviteService, this.asyncOperationService, this.inviteLinkService, undefined, undefined, undefined, this.analyticsService, this.entitiesService, this.foreignEntityLookup);
        }
        catch (error) {
            console.error('Failed to evaluate inline option condition:', error);
            return false;
        }
    }
    /**
     * Interpolate variables in a single rich text element
     */
    interpolateElement(element, context) {
        // Handle script elements by executing them (including empty ones)
        if (element.type === 'script') {
            return this.executeScriptBlock(element, context);
        }
        // Handle image elements with script-based imageId
        if (element.type === 'image' && element.imageIdIsScript && element.imageId) {
            return this.executeImageScript(element, context);
        }
        // Handle inventory-list elements with script-based inventoryOwner
        if (element.type === 'inventory-list' && element.inventoryOwnerIsScript && element.inventoryOwner) {
            return this.executeInventoryListScript(element, context);
        }
        // Handle location-with-portraits elements - enrich with portrait IDs at current location
        if (element.type === 'location-with-portraits') {
            return this.enrichLocationWithPortraits(element, context);
        }
        // Deep clone the element to avoid mutating the original
        const clonedElement = { ...element };
        // Interpolate content based on element type
        if (element.type === 'text' && typeof element.content === 'string') {
            clonedElement.content = this.interpolateString(element.content, context);
        }
        else if (element.type === 'text' && Array.isArray(element.content)) {
            // Recursively interpolate nested content in text elements
            clonedElement.content = element.content.map(contentItem => {
                if (typeof contentItem === 'string') {
                    return this.interpolateString(contentItem, context);
                }
                else if (contentItem && typeof contentItem === 'object' && 'type' in contentItem) {
                    return this.interpolateElement(contentItem, context);
                }
                return contentItem;
            });
        }
        else if (element.type === 'bold') {
            if (Array.isArray(element.content)) {
                clonedElement.content = this.interpolateElements(element.content, context);
            }
            else if (typeof element.content === 'string') {
                clonedElement.content = this.interpolateString(element.content, context);
            }
        }
        else if (element.type === 'italic') {
            if (Array.isArray(element.content)) {
                clonedElement.content = this.interpolateElements(element.content, context);
            }
            else if (typeof element.content === 'string') {
                clonedElement.content = this.interpolateString(element.content, context);
            }
        }
        else if (element.type === 'paragraph' && Array.isArray(element.content)) {
            // Use interpolateElements so inline-option conditions are evaluated
            clonedElement.content = this.interpolateElements(element.content, context);
        }
        else if (element.type === 'heading' && Array.isArray(element.content)) {
            // For heading elements, recursively interpolate nested elements
            clonedElement.content = element.content.map(contentItem => {
                if (typeof contentItem === 'string') {
                    return this.interpolateString(contentItem, context);
                }
                else if (contentItem && typeof contentItem === 'object' && 'type' in contentItem) {
                    return this.interpolateElement(contentItem, context);
                }
                return contentItem;
            });
        }
        else if (element.type === 'list' && Array.isArray(element.content)) {
            // For list elements, recursively interpolate nested elements
            clonedElement.content = element.content.map(contentItem => {
                if (typeof contentItem === 'string') {
                    return this.interpolateString(contentItem, context);
                }
                else if (contentItem && typeof contentItem === 'object' && 'type' in contentItem) {
                    return this.interpolateElement(contentItem, context);
                }
                return contentItem;
            });
        }
        else if (element.type === 'inventory-list') {
            // Interpolate inventory list attributes
            if (element.inventoryOwner) {
                clonedElement.inventoryOwner = this.interpolateString(element.inventoryOwner, context);
            }
            if (element.selectionTarget) {
                clonedElement.selectionTarget = this.interpolateString(element.selectionTarget, context);
            }
        }
        else if (element.type === 'scroll-area' && Array.isArray(element.content)) {
            clonedElement.content = this.interpolateElements(element.content, context);
        }
        else if (element.type === 'code-block' && typeof element.content === 'string') {
            clonedElement.content = this.interpolateString(element.content, context);
        }
        else if (element.type === 'character-comment') {
            if (element.character) {
                clonedElement.character = this.interpolateString(element.character, context);
            }
            if (element.portrait) {
                clonedElement.portrait = this.interpolateString(element.portrait, context);
            }
            if (element.text) {
                clonedElement.text = this.interpolateString(element.text, context);
            }
        }
        else if (element.type === 'feedback-card') {
            if (element.feedbackId) {
                clonedElement.feedbackId = this.interpolateString(element.feedbackId, context);
            }
            if (element.feedbackType) {
                clonedElement.feedbackType = this.interpolateString(element.feedbackType, context);
            }
            if (element.feedbackTitle) {
                clonedElement.feedbackTitle = this.interpolateString(element.feedbackTitle, context);
            }
            if (element.feedbackBy) {
                clonedElement.feedbackBy = this.interpolateString(element.feedbackBy, context);
            }
            if (element.feedbackAt) {
                clonedElement.feedbackAt = this.interpolateString(element.feedbackAt, context);
            }
            if (element.feedbackPreview) {
                clonedElement.feedbackPreview = this.interpolateString(element.feedbackPreview, context);
            }
            if (element.selectionTarget) {
                clonedElement.selectionTarget = this.interpolateString(element.selectionTarget, context);
            }
            if (element.feedbackScene) {
                clonedElement.feedbackScene = this.interpolateString(element.feedbackScene, context);
            }
            if (element.feedbackDeployment) {
                clonedElement.feedbackDeployment = this.interpolateString(element.feedbackDeployment, context);
            }
            if (element.feedbackAdventureDisplay) {
                clonedElement.feedbackAdventureDisplay = this.interpolateString(element.feedbackAdventureDisplay, context);
            }
        }
        else if (element.type === 'analytics-table-row') {
            if (element.analyticsPosition) {
                clonedElement.analyticsPosition = this.interpolateString(element.analyticsPosition, context);
            }
            if (element.analyticsName) {
                clonedElement.analyticsName = this.interpolateString(element.analyticsName, context);
            }
            if (element.analyticsValue) {
                clonedElement.analyticsValue = this.interpolateString(element.analyticsValue, context);
            }
        }
        else if (element.type === 'user-input' && element.userInputAttributes?.variable) {
            // Populate user-input element content from the variable store so the scene carries
            // the authoritative value. This lets the frontend correctly display (and clear)
            // the field based on backend state.
            const variableName = element.userInputAttributes.variable;
            const value = this.variableStore.getVariable(variableName, context);
            clonedElement.content =
                value !== undefined && value !== null ? String(value) : '';
        }
        else if (element.type === 'spinner' && element.spinnerAttributes?.variable) {
            const variableName = element.spinnerAttributes.variable;
            const raw = this.variableStore.getVariable(variableName, context);
            let value = raw !== undefined && raw !== null ? String(raw).trim() : '';
            // When the spinner shows a default/first option but the variable is still empty,
            // persist that value so scripts and option effects see it without a user click.
            if (value === '') {
                const valuesCsv = element.spinnerAttributes.values ?? '';
                const options = valuesCsv
                    .split(',')
                    .map((v) => v.trim())
                    .filter(Boolean);
                const defaultAttr = element.spinnerAttributes.default?.trim();
                const initial = defaultAttr && options.includes(defaultAttr) ? defaultAttr : options[0] ?? '';
                if (initial !== '') {
                    try {
                        this.variableStore.setVariable(variableName, initial, context, 'string');
                        value = initial;
                    }
                    catch {
                        // Variable not declared in this context; leave content empty.
                    }
                }
            }
            clonedElement.content = value;
        }
        else if (element.type === 'checkbox' && element.checkboxAttributes?.variable) {
            const attrs = element.checkboxAttributes;
            const variableName = attrs.variable;
            const raw = this.variableStore.getVariable(variableName, context);
            let value = raw !== undefined && raw !== null ? String(raw).trim() : '';
            // Persist default/unchecked when empty so Conditional scripts see a concrete value.
            if (value === '') {
                const checkedValue = attrs.checkedValue || '1';
                const uncheckedValue = attrs.uncheckedValue || '0';
                const defaultAttr = attrs.default?.trim();
                const initial = defaultAttr === checkedValue || defaultAttr === uncheckedValue
                    ? defaultAttr
                    : uncheckedValue;
                if (initial !== '') {
                    try {
                        this.variableStore.setVariable(variableName, initial, context, 'string');
                        value = initial;
                    }
                    catch {
                        // Variable not declared in this context; leave content empty.
                    }
                }
            }
            clonedElement.content = value;
        }
        else if (element.type === 'rating' && element.ratingAttributes?.variable) {
            const variableName = element.ratingAttributes.variable;
            const raw = this.variableStore.getVariable(variableName, context);
            const value = raw !== undefined && raw !== null ? String(raw).trim() : '';
            // Empty string is the unrated state; do not persist a default.
            clonedElement.content = value;
        }
        else if (element.type === 'color-sample' && element.colorSampleAttributes?.variable) {
            const variableName = element.colorSampleAttributes.variable;
            const value = this.variableStore.getVariable(variableName, context);
            clonedElement.content =
                value !== undefined && value !== null ? String(value) : '';
        }
        else if (element.type === 'code-display' && element.codeDisplayAttributes?.variable) {
            const variableName = element.codeDisplayAttributes.variable;
            const value = this.variableStore.getVariable(variableName, context);
            const fromVariable = value !== undefined && value !== null ? String(value).trim() : '';
            const preset = typeof element.content === 'string' ? String(element.content).trim() : '';
            clonedElement.content =
                fromVariable !== '' ? fromVariable : preset;
        }
        else if (element.type === 'color-picker' && element.colorPickerAttributes) {
            const attrs = element.colorPickerAttributes;
            clonedElement.colorPickerAttributes = {
                variable: attrs.variable,
                colors: this.interpolateString(attrs.colors, context),
                selectionTarget: this.interpolateString(attrs.selectionTarget, context)
            };
        }
        else if (element.type === 'time-picker' && element.timePickerAttributes?.variable) {
            const variableName = element.timePickerAttributes.variable;
            const value = this.variableStore.getVariable(variableName, context);
            clonedElement.content =
                value !== undefined && value !== null ? String(value) : '';
        }
        else if (element.type === 'date-picker' && element.datePickerAttributes?.variable) {
            const variableName = element.datePickerAttributes.variable;
            const value = this.variableStore.getVariable(variableName, context);
            clonedElement.content =
                value !== undefined && value !== null ? String(value) : '';
        }
        else if (element.type === 'inline-option' && Array.isArray(element.content)) {
            clonedElement.content = this.interpolateElements(element.content, context);
        }
        return clonedElement;
    }
    /**
     * Extract variable references from text
     */
    extractVariableReferences(text) {
        if (!text || typeof text !== 'string') {
            return [];
        }
        const variablePattern = /\$\{([^}]+)\}/g;
        const references = [];
        let match;
        while ((match = variablePattern.exec(text)) !== null) {
            references.push(match[1].trim());
        }
        return references;
    }
    /**
     * Check if text contains variable references
     */
    hasVariableReferences(text) {
        if (!text || typeof text !== 'string') {
            return false;
        }
        return /\$\{([^}]+)\}/.test(text);
    }
    /**
     * Validate that a variable value doesn't contain variable references
     */
    validateVariableValue(value) {
        if (typeof value === 'string') {
            return !this.hasVariableReferences(value);
        }
        return true; // Non-string values are always valid
    }
    /**
     * Whether a script return string should be re-parsed as rich text (markdown, tags, multiline).
     */
    scriptResultShouldParseAsRichText(text) {
        const trimmed = text.trim();
        if (!trimmed) {
            return false;
        }
        if (trimmed.includes('<') && trimmed.includes('>')) {
            return true;
        }
        if (/\n/.test(trimmed)) {
            return true;
        }
        if (/\*\*[^*]+\*\*/.test(trimmed)) {
            return true;
        }
        if (/(?<!\*)\*[^*\n]+\*(?!\*)/.test(trimmed)) {
            return true;
        }
        if (/^#+\s/m.test(trimmed)) {
            return true;
        }
        return false;
    }
    /**
     * Execute a script block and replace it with text element containing the result
     */
    executeScriptBlock(element, context) {
        if (!element.scriptCode || element.scriptCode.trim() === '') {
            return {
                type: 'text',
                content: ''
            };
        }
        // If we don't have player and gameSession, we can't execute scripts
        if (!context.player || !context.gameSession) {
            console.warn('Cannot execute script block: missing player or gameSession context');
            return {
                type: 'text',
                content: '[Error: Script execution failed - missing context]'
            };
        }
        try {
            let scriptSource = element.scriptCode;
            if (context.injectedVariables && Object.keys(context.injectedVariables).length > 0) {
                scriptSource = (0, sharedDataRichText_1.prepareScriptCodeWithInjectedVariables)(scriptSource, context.injectedVariables);
            }
            const result = this.executeScriptWithReturnValue(scriptSource, context.player, context.gameSession, this.variableStore);
            const stringInterpolationContext = {
                playerId: context.playerId,
                characterName: context.characterName,
                currentSceneId: context.currentSceneId,
                userId: context.userId
            };
            if ((0, utils_1.isRichTextDescription)(result)) {
                const interpolated = this.interpolateRichText(result, {
                    ...stringInterpolationContext,
                    player: context.player,
                    gameSession: context.gameSession
                });
                if (typeof interpolated === 'string') {
                    return {
                        type: 'text',
                        content: this.interpolateString(interpolated, stringInterpolationContext)
                    };
                }
                return {
                    type: 'script',
                    content: interpolated.elements
                };
            }
            // Convert result to string (for backward compatibility)
            const resultString = result !== null && result !== undefined ? String(result) : '';
            const interpolatedResult = this.interpolateString(resultString, stringInterpolationContext);
            // Re-parse when output contains rich tags, line breaks, or markdown (e.g. **bold** from adventure scripts).
            if (this.scriptResultShouldParseAsRichText(interpolatedResult)) {
                try {
                    const parsed = RichTextParser_1.RichTextParser.parseRichTextDescription(interpolatedResult);
                    if (parsed.elements && parsed.elements.length > 0) {
                        const interpolatedElements = this.interpolateElements(parsed.elements, {
                            ...stringInterpolationContext,
                            player: context.player,
                            gameSession: context.gameSession
                        });
                        return {
                            type: 'script',
                            content: interpolatedElements
                        };
                    }
                }
                catch {
                    // Fall through to single text element if re-parse fails
                }
            }
            // Return a text element with the script result (after interpolation)
            return {
                type: 'text',
                content: interpolatedResult
            };
        }
        catch (error) {
            console.error('Script block execution failed:', error);
            // Return error message as text
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            return {
                type: 'text',
                content: `[Error: ${errorMessage}]`
            };
        }
    }
    /**
     * Execute a script in an image element and replace the imageId with the result
     */
    executeImageScript(element, context) {
        if (!element.imageId) {
            return element;
        }
        // If we don't have player and gameSession, we can't execute scripts
        if (!context.player || !context.gameSession) {
            console.warn('Cannot execute image script: missing player or gameSession context');
            return element;
        }
        try {
            // Execute the script using ScriptEvaluator
            const result = this.executeScriptWithReturnValue(element.imageId, context.player, context.gameSession, this.variableStore);
            // Convert result to string (the imageId)
            const imageId = result !== null && result !== undefined ? String(result) : '';
            // Return a new image element with the resolved imageId, preserving the variant
            return {
                type: 'image',
                imageId: imageId,
                imageType: element.imageType,
                imageVariant: element.imageVariant, // Preserve the variant (e.g., 'large')
                content: ''
            };
        }
        catch (error) {
            console.error('Image script execution failed:', error);
            // Return the original element if script execution fails
            return element;
        }
    }
    enrichLocationWithPortraits(element, context) {
        if (!context.player || !context.gameSession || !this.adventureManager) {
            return element;
        }
        const adventure = this.adventureManager.getAdventure(context.gameSession.adventureId);
        if (!adventure) {
            return element;
        }
        let locationTag;
        if (element.locationWithPortraitsLocationTagIsScript && element.locationWithPortraitsLocationTag) {
            try {
                const result = this.executeScriptWithReturnValue(element.locationWithPortraitsLocationTag.slice(3, -3), context.player, context.gameSession, this.variableStore);
                locationTag = result !== null && result !== undefined ? String(result).trim() : undefined;
            }
            catch (error) {
                console.error('Failed to evaluate Location-With-Portraits location script:', error);
                return { ...element, locationWithPortraitsImageId: undefined, locationWithPortraitsPortraitIds: [] };
            }
        }
        else if (element.locationWithPortraitsLocationTag) {
            locationTag = element.locationWithPortraitsLocationTag;
        }
        else {
            locationTag = context.player.currentScene
                ? adventure.getScene(context.player.currentScene)?.locationTag
                : undefined;
            if (!locationTag) {
                const scriptContext = this.createScriptContext(context.player, context.gameSession, this.variableStore);
                const current = scriptContext.getCurrentLocation?.();
                locationTag = typeof current === 'string' ? current.trim() : undefined;
            }
        }
        if (!locationTag) {
            return { ...element, locationWithPortraitsImageId: undefined, locationWithPortraitsPortraitIds: [] };
        }
        const location = adventure.getLocation(locationTag);
        const imageId = location?.locationImageId ?? `${context.gameSession.adventureId}:${locationTag}`;
        try {
            const scriptContext = this.createScriptContext(context.player, context.gameSession, this.variableStore);
            const portraitTags = scriptContext.getCharacterPortraitsAtLocation(locationTag, context.player.characterName);
            if (!portraitTags || typeof portraitTags !== 'string') {
                return { ...element, locationWithPortraitsImageId: imageId, locationWithPortraitsPortraitIds: [] };
            }
            const parsed = RichTextParser_1.RichTextParser.parseRichTextDescription(portraitTags);
            const portraitIds = [];
            for (const el of parsed.elements || []) {
                if (el?.type === 'image' && el?.imageType === 'portrait' && el?.imageId) {
                    portraitIds.push(el.imageId);
                }
                if (el?.type === 'paragraph' && Array.isArray(el.content)) {
                    for (const child of el.content) {
                        if (child?.type === 'image' && child?.imageType === 'portrait' && child?.imageId) {
                            portraitIds.push(child.imageId);
                        }
                    }
                }
            }
            return { ...element, locationWithPortraitsImageId: imageId, locationWithPortraitsPortraitIds: portraitIds };
        }
        catch (error) {
            console.error('Failed to enrich Location-With-Portraits:', error);
            return { ...element, locationWithPortraitsImageId: imageId, locationWithPortraitsPortraitIds: [] };
        }
    }
    executeInventoryListScript(element, context) {
        if (!element.inventoryOwner) {
            return element;
        }
        // If we don't have player and gameSession, we can't execute scripts
        if (!context.player || !context.gameSession) {
            console.warn('Cannot execute inventory list script: missing player or gameSession context');
            return element;
        }
        try {
            // Execute the script using ScriptEvaluator
            const result = this.executeScriptWithReturnValue(element.inventoryOwner, context.player, context.gameSession, this.variableStore);
            // Convert result to string (the inventory owner)
            const inventoryOwner = result !== null && result !== undefined ? String(result) : '';
            // Return a new inventory-list element with the resolved inventoryOwner
            return {
                type: 'inventory-list',
                inventoryType: element.inventoryType,
                inventoryOwner: inventoryOwner,
                selectionTarget: element.selectionTarget,
                content: ''
            };
        }
        catch (error) {
            console.error('Inventory list script execution failed:', error);
            // Return the original element if script execution fails
            return element;
        }
    }
    /**
     * Execute script and return its value (description blocks, image ids, etc.).
     * Delegates to {@link ScriptEvaluator} so adventure `## Scripts` prelude and validation apply.
     */
    executeScriptWithReturnValue(script, player, gameSession, variableStore) {
        return ScriptEvaluator_1.ScriptEvaluator.executeScriptWithReturnValue(script, player, gameSession, variableStore ?? this.variableStore, this.adventureManager, this.friendsService, this.adventureInviteService, this.asyncOperationService, this.inviteLinkService, undefined, undefined, undefined, this.analyticsService, this.entitiesService, this.foreignEntityLookup);
    }
    /**
     * Create script execution context with helper functions
     */
    createScriptContext(player, gameSession, variableStore) {
        const store = variableStore ?? this.variableStore;
        if (!this.adventureManager) {
            console.warn('AdventureManager not available for script execution');
        }
        const runConditionForPlayer = (script, targetPlayer) => ScriptEvaluator_1.ScriptEvaluator.executeCondition(script, targetPlayer, gameSession, store, this.adventureManager, this.friendsService, this.adventureInviteService, this.asyncOperationService, this.inviteLinkService, undefined, undefined, undefined, this.analyticsService, this.entitiesService, this.foreignEntityLookup);
        return (0, ScriptContext_1.createReadOnlyScriptContext)({
            player,
            gameSession,
            variableStore: store,
            adventureManager: this.adventureManager,
            friendsService: this.friendsService,
            adventureInviteService: this.adventureInviteService,
            asyncOperationService: this.asyncOperationService,
            inviteLinkService: this.inviteLinkService,
            analyticsService: this.analyticsService,
            evaluateConditionScript: (script) => runConditionForPlayer(script, player),
            executeConditionForPlayer: runConditionForPlayer,
            getNowMs: () => ScriptEvaluator_1.ScriptEvaluator.resolveNowMs(),
            entitiesService: this.entitiesService,
            foreignEntityLookup: this.foreignEntityLookup
        });
    }
    /**
     * Interpolate a Shared-Data block subtree after shared aggregates are loaded.
     * Scripts execute with injected shared_* values; results are expanded like normal scene interpolation.
     */
    interpolateSharedDataElements(elements, context) {
        return this.interpolateElements(elements, context);
    }
}
exports.InterpolationPipeline = InterpolationPipeline;
//# sourceMappingURL=InterpolationPipeline.js.map